import { normalize } from "pathe";
import { z } from "zod/v4";

import { pageMetadata } from "./pageMetadata";

const publishSettings = z.object({
	template: z.union([z.literal("blog"), z.literal("tech-docs-vitepress")]).optional(),
	logo: z
		.object({
			dark: z.string().optional(),
			light: z.string().optional(),
		})
		.optional(),
	favicon: z.string().optional(),
	urlPrefix: z.string().optional(),
	topbarLinks: z
		.array(
			z
				.object({
					name: z.string(),
					url: z.string(),
				})
				.nullish()
		)
		.optional(),
	topbarCtaButton: z
		.object({
			name: z.string(),
			url: z.string(),
		})
		.optional(),
	footerSocials: z
		.object({
			x: z.string().optional(),
			bluesky: z.string().optional(),
			github: z.string().optional(),
			linkedin: z.string().optional(),
		})
		.optional(),
});

export const baseSpecSchema = z.object({
	tables: z.record(
		z.string(),
		z.object({
			primaryKey: z.string(),
		})
	),
});

export const emptyBaseSpec = (): BaseSpec => ({ tables: {} });

export type PublishSettings = z.infer<typeof publishSettings>;
export type BaseSpec = z.infer<typeof baseSpecSchema>;

export const makeUrlPrefix = (
	workspaceName: string,
	documentId: string,
	publishSettings: PublishSettings | undefined
) => {
	const defaultUrlPrefix = getDefaultUrlPrefix(workspaceName, documentId);

	const urlPrefix = publishSettings?.urlPrefix;

	if (!urlPrefix) {
		return defaultUrlPrefix;
	}

	return urlPrefix;
};

export const getDefaultUrlPrefix = (workspaceName: string, documentId: string) => {
	return `/d/${workspaceName}/${documentId}`;
};

export const documentTypeSchema = z.enum(["notes", "template"]);
export type DocumentType = z.infer<typeof documentTypeSchema>;

/**
 * A direct mirror of the GraphQL `CanvasMetadata` type
 */
export const DocumentMetadataSchema = z
	.object({
		id: z.string(),
		schemaVersion: z.number().default(0),

		title: z.string(),
		description: z.string().optional(),
		prettyName: z.string().optional(),
		created: z.coerce.date().default(new Date()),
		// Default `updated` to `created` when missing, with fallback to now handled below
		updated: z.coerce.date().optional(),

		type: documentTypeSchema.optional(),

		labels: z
			.array(z.string())
			.nullish()
			.transform((v) => v ?? []),

		os: z
			.array(z.string())
			.nullish()
			.transform((v) => v ?? []),

		pagesMetadata: z.array(pageMetadata),

		publishSettings: publishSettings.optional(),

		base: baseSpecSchema.optional(),
	})
	.transform((data) => {
		// If `updated` is not provided, use `created`; if `created` somehow isn't set, fallback to now
		const created = data.created ?? new Date();
		const updated = data.updated ?? created ?? new Date();

		return { ...data, created, updated };
	});

export const messageIdempotentInitMetadataRoomResponseAccepted = z.object({
	type: z.literal("idempotent-init-metadata-room-response-accepted"),
	version: z.number(),
	canvasMetadata: DocumentMetadataSchema,
	page: z.unknown(),
});

export type MessageInitMetadataRoomResponseAccepted = z.infer<
	typeof messageIdempotentInitMetadataRoomResponseAccepted
>;

export const messageIdempotentInitMetadataRoomResponseRejected = z.object({
	type: z.literal("idempotent-init-metadata-room-response-rejected"),
	version: z.number(),
});

export type MessageInitMetadataRoomResponseRejected = z.infer<
	typeof messageIdempotentInitMetadataRoomResponseRejected
>;

/**
 * A direct mirror of the GraphQL `CanvasMetadata` type.
 */
export type DocumentMetadata = z.infer<typeof DocumentMetadataSchema>;

export type DocumentPageMetadata = DocumentMetadata["pagesMetadata"][number];

/**
 * Takes a path to a page file and a `DocumentPageMetadata[]` and resolves that to a page ID. This
 * is nontrivial because the `pagesMetadata` defines the path a page is found out, and only from
 * that information can we find the page ID.
 */
export function resolveFilePathToPage(
	absolutePath: string,
	documentId: string,
	documentRootPath: string,
	pagesMetadata: readonly DocumentPageMetadata[]
): { documentId: string; pageId: string } | null {
	const normalizedAbsolute = normalize(absolutePath);
	const normalizedRootBase = normalize(documentRootPath);
	const normalizedRoot = normalizedRootBase.endsWith("/")
		? normalizedRootBase
		: normalizedRootBase + "/";

	if (!normalizedAbsolute.startsWith(normalizedRoot)) {
		return null;
	}

	const relativePath = normalizedAbsolute.slice(normalizedRoot.length);

	for (const page of pagesMetadata) {
		const normalizedPagePath = page.path ? normalize(page.path) : undefined;
		if (normalizedPagePath === relativePath) {
			return { documentId, pageId: page.id };
		}
	}

	const pagesMatch = relativePath.match(/^pages\/([^/]+)\/index\.(md|yml|yaml)$/);
	if (pagesMatch) {
		const potentialPageId = pagesMatch[1];
		const matchingPage = pagesMetadata.find((p) => p.id === potentialPageId);
		if (matchingPage) {
			return { documentId, pageId: matchingPage.id };
		}
	}

	return null;
}
